import { Injectable } from '@angular/core';
import { HttpHeaders, HttpClient } from '@angular/common/http';
import { map, catchError, tap } from 'rxjs/operators';
import { ConfigurationService } from './configuration.service';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import {
  FileTypeEnum,
  FileUploadStatusEnum,
  IFile,
  fileEntityTypes,
} from '@app/shared/components/file-upload/constants';
import { DataService } from '../data/data.service';
import { DrawerService } from '@app/shared/components/drawer/drawer.service';
import { ExportToDocumentService } from './export-to-document.service';
import { Router } from '@angular/router';
import { SnackBarService } from '@app/shared/components/snackbar/snackbar.service';
import { SnackBarsTypes } from '@app/shared/enums/snackbars';
import { CmUploadFlowComponent } from '@app/shared/components/cm-upload-flow/cm-upload-flow.component';
import { ImportError } from '@app/shared/models/file-upload-error-response.model';
import { IQuery } from '@app/shared/interfaces/query';
import { UtilService } from './util.service';
import { FileUploadResponse, FileUploadResponseStatusEnum } from '@app/shared/models/file-upload-response.model';
import { FileResponse } from '@app/shared/models/fileResponse';

@Injectable({
  providedIn: 'root',
})
export class FileUploadService {
  private uploadStatusSubject = new BehaviorSubject<{
    file: IFile;
    uploadStatus: FileUploadStatusEnum;
  }>({ file: undefined, uploadStatus: undefined });
  private successUploadSubject = new Subject<string>();
  private importProgressSubject = new BehaviorSubject<{ processedCount: number; totalCount: number } | null>(null);
  private _files: IFile[] = [];
  uploadSizeLimitMB = 25;
  parsingErrorText = 'File parsing error';

  readonly uploadStatus$ = this.uploadStatusSubject.asObservable();
  readonly sucessUpload$ = this.successUploadSubject.asObservable();
  readonly importProgress$ = this.importProgressSubject.asObservable();

  constructor(
    private http: HttpClient,
    private configurationService: ConfigurationService,
    private dataService: DataService,
    private drawerService: DrawerService,
    private exportToDocumentService: ExportToDocumentService,
    private router: Router,
    private snackBarService: SnackBarService
  ) {}

  fileProcessingStarted(index: number) {
    const file = this._files.find((fi) => fi.index === index);

    this.setFiles([
      ...this._files.filter((fi) => fi.index !== index),
      { ...file, status: FileUploadStatusEnum.INPROCESS },
    ]);

    this.uploadStatusSubject.next({
      file: file,
      uploadStatus: FileUploadStatusEnum.INPROCESS,
    });
  }

  fileProcessingFinished(collection: string) {
    const file = this.getFiles(collection).find((fi) => fi.status === FileUploadStatusEnum.INPROCESS);

    if (file) {
      this.setFiles([
        ...this._files.filter((fi) => fi.index !== file.index),
        { ...file, status: FileUploadStatusEnum.FINISHED },
      ]);

      this.uploadStatusSubject.next({
        file,
        uploadStatus: FileUploadStatusEnum.FINISHED,
      });
    }
  }

  displayStatus(title: string, description: string, type: SnackBarsTypes, action?: string, customAction?: () => void) {
    this.snackBarService.openSnackBar({
      type,
      title,
      description,
      action,
      customAction,
    });
  }

  onResolveErrors(importId: string) {
    const file = this.getFile(null, importId);

    this.router.navigate([fileEntityTypes[file.fileEntity.entity].route]);
    const currentFile = file;

    this.drawerService.open(CmUploadFlowComponent, {
      title: currentFile.fileEntity.title,
      entity: currentFile.fileEntity.entity,
      collection: currentFile.fileEntity.collection,
      action: currentFile.fileEntity.action,
    });
  }

  getFiles(entityName?: string, fileType?: FileTypeEnum): IFile[] {
    return [...(this._files || [])].filter(
      (file) =>
        (!entityName ||
          file.fileEntity?.entity == entityName ||
          file.fileEntity?.collection == entityName ||
          file.fileEntity?.entitySingular == entityName) &&
        (!fileType || file.type == fileType)
    );
  }

  removeTempAttachments() {
    const files = this.getFiles().filter(
      (file) => file.type !== FileTypeEnum.DOCUMENTS && file.type !== FileTypeEnum.IMAGES
    );

    this.setFiles(files);
  }

  getNextIndex(): number {
    const files = this.getFiles().filter((f) => !!f.index);

    return files.length === 0 ? 1 : Math.max(...files.map((file) => file.index)) + 1;
  }

  getFile(index?: number, importId?: string) {
    return [...(this._files || [])].find(
      (file) => (!index || file.index == index) && (!importId || file.importId == importId)
    );
  }

  addFile(file: any) {
    this._files = [...this._files, file];
  }

  setFileToUpload(index: number, allowMultiple?: boolean) {
    const file = this.getFile(index);
    this.setFiles([
      ...this._files.filter((f) => f.index !== index),
      { ...file, status: FileUploadStatusEnum.INPROCESS },
    ]);
  }

  setFiles(files: any[]) {
    this._files = [...files];
  }

  removeFile(index: number): void {
    const fileIndex = this._files.findIndex((f) => f.index === index);
    this._files.splice(fileIndex, 1);
  }

  getImportErrorList(query: IQuery, importId: string, entity: string): Observable<ImportError[]> {
    const url = `Imports/${importId}/errorList/${entity}`;
    const configUrl = UtilService.fillUrl(query);

    return this.dataService.get(url + configUrl).pipe(
      map((errors) => {
        return errors.map((error) => ({
          _id: error._id,
          validations: [...error.validations],
        }));
      })
    );
  }

  exportErrors(fileIndex: number, fileWithError) {
    const file = fileWithError || this.getFile(fileIndex);
    const headers = ['row', 'column', 'error'];
    const fileName = `${file.name.split('.')[0]}_errors.csv`;

    this.getImportErrorList({}, file.importId, file.fileEntity.entity)
      .pipe(
        tap((errors) => {
          const data = errors
            .map((error) => {
              return error.validations.map((validation) => ({
                row: validation._id,
                column: validation.property,
                error: validation.message,
              }));
            })
            .flat();
          this.exportToDocumentService.exportToCsv(data, fileName, headers);
        })
      )
      .subscribe();
  }

  isProcessingFile(entityName?: string): boolean {
    return (
      [...(this._files || [])].filter(
        (file) =>
          (!entityName || file.fileEntity?.entity == entityName) &&
          file.uploadFlow &&
          file.status === FileUploadStatusEnum.INPROCESS
      ).length > 0
    );
  }

  isFilePending(entityName?: string): boolean {
    return (
      [...(this._files || [])].filter(
        (file) =>
          (!entityName || file.fileEntity?.entity == entityName) &&
          (file.status === FileUploadStatusEnum.INPROCESS || file.status === FileUploadStatusEnum.FAILED) &&
          file.uploadFlow
      ).length > 0
    );
  }

  uploadIsCanceled(index: number): boolean {
    return this.getFiles().find((file) => file.index === index)?.status === FileUploadStatusEnum.CANCELED;
  }

  uploadFailed(entity?: string, index?: number, importId?: string): boolean {
    if (entity) return this.getFiles(entity).filter((file) => file.status === FileUploadStatusEnum.FAILED).length > 0;

    return (
      this.getFiles().find((file) => (!index || file.index === index) && (!importId || file.importId === importId))
        ?.status === FileUploadStatusEnum.FAILED
    );
  }

  cancelFileUpload(index: number) {
    const files = this.getFiles().filter((fi) => fi.index !== index);

    this.setFiles([...files]);

    this.uploadStatusSubject.next({
      file: undefined,
      uploadStatus: undefined,
    });
  }

  setImportId(importId: string, collection: string) {
    const file = this.getFiles(collection)[0];
    if (!file) return;
    const files = this.getFiles().filter((fi) => fi.index !== file.index);

    this.setFiles([...files, { ...file, importId }]);
  }

  reset(entityName: string) {
    this.setFiles([
      ...this.getFiles().filter(
        (file) => !entityName || (file.fileEntity?.entity !== entityName && file.fileEntity?.collection !== entityName)
      ),
    ]);

    this.uploadStatusSubject.next({
      file: undefined,
      uploadStatus: undefined,
    });
  }

  setImportToFailed(importId: string, hasParsingError: boolean, hasParsingErrorDetail: string) {
    const file = this.getFile(null, importId);
    const files = this.getFiles().filter((fi) => fi.importId !== importId);

    this.setFiles([
      ...files,
      {
        ...file,
        status: FileUploadStatusEnum.FAILED,
        hasParsingError,
        hasParsingErrorDetail,
      },
    ]);

    this.uploadStatusSubject.next({
      file,
      uploadStatus: FileUploadStatusEnum.FAILED,
    });
  }

  uploadFinished(index: number) {
    const file = this.getFile(index);
    if (!file) return;

    const files = this.getFiles().filter((fi) => fi.index !== index);

    this.setFiles([...files, { ...file, status: undefined }]);
  }

  onFileProcessing(importStatus: FileUploadResponse) {
    const { status, parameters, _id } = importStatus;
    const { collection } = parameters;

    this.uploadStatusSubject.next({
      file: undefined,
      uploadStatus: FileUploadStatusEnum.INPROCESS,
    });

    this.setImportId(_id, collection);

    if (status == FileUploadResponseStatusEnum.IMPORTING) {
      this.importProgressSubject.next({
        processedCount: importStatus.processedCount ?? 0,
        totalCount: importStatus.totalCount ?? 0,
      });
      return;
    } else if (status == FileUploadResponseStatusEnum.FileUploaded) {
      this.displayStatus('file-upload.file-info-header', 'file-upload.file-info-description', SnackBarsTypes.INFO);
    } else if (
      status == FileUploadResponseStatusEnum.Failed ||
      status == FileUploadResponseStatusEnum.FileReviewFailed
    ) {
      this.importProgressSubject.next(null);
      if (importStatus.statusDetail?.split(',').length > 0) {
        const hasParsingError = importStatus.statusDetail?.split(',')[0] === this.parsingErrorText;

        let errorMessage = importStatus.statusDetail?.split(',')[1] || importStatus.statusDetail?.split(',')[0];
        const isCsvError = errorMessage.indexOf('csv') > -1;
        const noRecordsError = (errorMessage || '').split('.')[0];
        errorMessage = isCsvError ? errorMessage : noRecordsError;

        this.setImportToFailed(_id, hasParsingError, errorMessage + '.');
      }

      this.displayStatus(
        'file-upload.file-error-header',
        'file-upload.file-error-description',
        SnackBarsTypes.ERROR,
        'file-upload.file-error-resolve-label',
        this.onResolveErrors.bind(this, _id)
      );
    } else if (status == FileUploadResponseStatusEnum.COMPLETED) {
      this.importProgressSubject.next(null);
      if (this.uploadFailed(null, null, _id)) {
        this.fileProcessingFinished(parameters.collection);
      } else {
        this.displayStatus(
          'file-upload.file-success-header',
          'file-upload.file-success-description',
          SnackBarsTypes.SUCCESS
        );

        this.fileProcessingFinished(parameters.collection);
        this.reset(collection);
        this.successUploadSubject.next(parameters.collection);
      }
    } else if (status == FileUploadResponseStatusEnum.CompletedWithConflicts) {
      this.displayStatus(
        'file-upload.conflict-header',
        'file-upload.conflict-description',
        SnackBarsTypes.WARNING,
        'file-upload.conflict-action-label',
        () => this.router.navigate(['/admin-station/import-conflicts'])
      );
      this.fileProcessingFinished(parameters.collection);
      this.reset(collection);
      this.successUploadSubject.next(parameters.collection);
    }
  }

  getCurrentFile(entity: string) {
    return this.getFiles(entity).find(
      (file) => file.status === FileUploadStatusEnum.INPROCESS || file.status === FileUploadStatusEnum.FAILED
    );
  }

  hasImportFailed(entity: string): boolean {
    return !!this.getFiles(entity).find((file) => file.status === FileUploadStatusEnum.FAILED);
  }

  isUploadingFile(index: number) {
    return this.getFiles().find((file) => file.index === index)?.status === FileUploadStatusEnum.UPLOADING;
  }

  uploadFile(url: string, file: any, importId?: string, collection?: string) {
    url = this.configurationService.apiUrl + url;
    const options = {};
    const input = new FormData();
    input.append('file', file);
    input.append('generateName', 'true');
    if (importId) {
      input.append('importId', importId);
    }
    if (collection) {
      input.append('collectionName', collection);
    }
    options['headers'] = new HttpHeaders()
      .append('Accept', 'application/json')
      .append('Content-Disposition', 'multipart/form-data');

    return this.http.post(url, input, options).pipe(
      map((res: FileResponse) => res),
      catchError((error) => {
        // eslint-disable-next-line
        console.error('Client side network error occurred:', error.error.message);
        return this.dataService.handleHttpError(error);
      })
    );
  }

  uploadUrl(url: string) {
    const options = {};
    url = this.configurationService.apiUrl + url;
    options['headers'] = new HttpHeaders()
      .append('Accept', 'application/json')
      .append('Content-Disposition', 'multipart/form-data');

    return this.http.post(url, null, options).pipe(
      map((res: FileResponse) => res),
      catchError((error) => {
        //eslint-disable-next-line
        console.error('Client side network error occurred:', error.error.message);
        return this.dataService.handleHttpError(error);
      })
    );
  }

  deleteFile(url: string, fileName: string) {
    const options = {};
    url = this.configurationService.apiUrl + url;
    options['headers'] = new HttpHeaders().append('Accept', 'application/json');
    // eslint-disable-next-line
    console.log('data.service deleting');

    return this.http.delete(url + fileName, options).subscribe(() => {
      // eslint-disable-next-line
      console.log('deleted');
    });
  }
}
